/**
 * Générateur — Simplifier l'écriture de sommes comportant des parenthèses.
 * Attendu ID : 712 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 5)
 *
 * Plan de cartes :
 *   « simplifier » (Ex.6bis, sens parenthèses → simplifié) : les TROUS sont
 *     les SIGNES de l'écriture simplifiée (saisie d'un caractère + ou -) —
 *     jamais de saisie d'expression complète (les espaces casseraient le
 *     scoring).
 *   « reparentheser » (Ex.6bis, sens simplifié → parenthèses — le doc signale
 *     plusieurs écritures acceptables : la FORME EST IMPOSÉE par la phrase,
 *     « + ( {{-3}} ) ») — texte_trou_drag.
 *   « transformer » (Ex.9) : transformation + résultat (pont O20/O22) —
 *     texte_trou_saisie (deux trous : terme transformé puis résultat).
 *   « suite » (Ex.5bis) : transformer toute l'expression en suite
 *     d'additions (3 à 5 termes) — texte_trou_drag.
 *
 * Politique : signes imposés, tiret clavier, dixièmes entiers.
 */

import {
    randomChoice, randInt, shuffle,
    makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 712;
const L_SIMPLIFIER = 'Écrire sans parenthèses (écriture simplifiée)';
const L_REPARENTHESER = 'Réécrire avec des parenthèses';
const L_TRANSFORMER = 'Transformer puis calculer';
const L_SUITE = 'Transformer en suite d\'additions';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const frD = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`);
};
const signeD = (d) => (d === 0 ? '0' : d < 0 ? frD(d) : `+${frD(d)}`);
const parenD = (d) => `(${signeD(d)})`;

/**
 * Tire une expression : liste de CONTRIBUTIONS (valeurs signées, dixièmes) et
 * son affichage parenthésé — chaque terme après le premier est « op (terme) »
 * ou « op terme nu » (rotation des formes du cadrage).
 */
const tirerExpression = (nMin, nMax, decimales) => {
    const n = randInt(nMin, nMax);
    const contribs = [];
    const absolues = new Set();
    for (let essai = 0; essai < 80 && contribs.length < n; essai++) {
        const v = (decimales && randomChoice([true, false])) ? randInt(11, 99) : randInt(2, 25) * 10;
        if (!absolues.has(v)) {
            absolues.add(v);
            contribs.push((randomChoice([true, false]) ? 1 : -1) * v);
        }
    }
    [30, -80, 110, -170, 220].forEach(v => {
        if (contribs.length < n && !absolues.has(Math.abs(v))) { absolues.add(Math.abs(v)); contribs.push(v); }
    });

    // affichage : op ∈ {+, -} tiré, terme = contribution vue à travers l'op
    const morceaux = [frD(contribs[0])];
    const formes = [null];
    for (let i = 1; i < contribs.length; i++) {
        const op = randomChoice(['+', '-']);
        const terme = op === '+' ? contribs[i] : -contribs[i];
        const nu = terme > 0 && randomChoice([true, false]);
        morceaux.push(`${op} ${nu ? frD(terme) : parenD(terme)}`);
        formes.push(op);
    }
    return { contribs, expr: morceaux.join(' '), formes };
};

// ═════════ Carte 1 — vers l'écriture simplifiée (Ex.6bis a/b/f) ═════════

const genSimplifierSaisie = () => {
    const { contribs, expr } = tirerExpression(3, 4, false);
    const rhs = contribs.map((c, i) => (i === 0
        ? frD(c)
        : `{{${c > 0 ? '+' : '-'}}} ${frD(Math.abs(c))}`)).join(' ');
    const payload = makeTexteTrou(`${expr} = ${rhs}`,
        '<p><b>Écris cette somme en écriture simplifiée : complète chaque signe</b> (tape + ou -).</p>',
        { ...opts(L_SIMPLIFIER), mode: 'saisie' });
    payload._meta.params = { contribs };
    return payload;
};

// ═════════ Carte 2 — vers l'écriture parenthésée (Ex.6bis c/d/e/g) ═════════

const genReparentheserDrag = () => {
    // expression SIMPLIFIÉE en entrée : contributions affichées nues
    const { contribs } = tirerExpression(3, 4, false);
    const gauche = contribs.map((c, i) => (i === 0 ? frD(c) : `${c > 0 ? '+' : '-'} ${frD(Math.abs(c))}`)).join(' ');
    // forme imposée : somme de relatifs parenthésés (sauf le premier)
    const droite = contribs.map((c, i) => (i === 0 ? frD(c) : `+ ( {{${signeD(c)}}} )`)).join(' ');
    const phrase = `${gauche} = ${droite}`;
    const candidats = contribs.slice(1).map(c => signeD(-c));
    const payload = makeTexteTrou(phrase,
        '<p><b>Réécris cette expression comme une somme de nombres relatifs entre parenthèses.</b></p>',
        { ...opts(L_REPARENTHESER), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, candidats).slice(0, 3) });
    payload._meta.params = { contribs };
    return payload;
};

// ═════════ Carte 3 — transformer puis calculer (Ex.9) ═════════

const genTransformerCalculer = () => {
    const decimal = randomChoice([true, false]);
    const v = () => (decimal ? randInt(11, 199) : randInt(2, 45) * 10);
    const a = (randomChoice([true, false]) ? 1 : -1) * v();
    const b = (randomChoice([true, false]) ? 1 : -1) * v();
    const phrase = `${parenD(a)} - ${parenD(b)} = ${parenD(a)} + ( {{${signeD(-b)}}} ) = {{${signeD(a - b)}}}`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Transforme la soustraction en addition de l\'opposé, puis calcule.</b> '
        + 'Écris chaque réponse avec son signe (ex. : -5 ou +8), ou 0 si elle est nulle.</p>',
        { ...opts(L_TRANSFORMER), mode: 'saisie' });
    payload._meta.params = { a, b };
    return payload;
};

// ═════════ Carte 4 — suite d'additions (Ex.5bis) ═════════

const genSuiteDrag = () => {
    const { contribs } = tirerExpression(3, 5, false);
    // au moins DEUX soustractions garanties (sinon : phrase sans trou)
    const indices = contribs.map((_, i) => i).slice(1);
    const forcees = new Set(shuffle(indices).slice(0, Math.min(2, indices.length)));
    const formes = contribs.map((_, i) => (i === 0 ? null : (forcees.has(i) ? '-' : randomChoice(['+', '-']))));
    const expr = contribs.map((c, i) => {
        if (i === 0) return frD(c);
        const terme = formes[i] === '+' ? c : -c;
        return `${formes[i]} ${terme > 0 && formes[i] === '+' && randomChoice([true, false]) ? frD(terme) : parenD(terme)}`;
    }).join(' ');
    // suite d'additions : chaque terme après le premier devient « + relatif » ;
    // seuls les termes issus d'une SOUSTRACTION sont à trouver
    const droite = contribs.map((c, i) => {
        if (i === 0) return frD(c);
        return formes[i] === '-' ? `+ {{${signeD(c)}}}` : `+ ${parenD(c)}`;
    }).join(' ');
    const phrase = `${expr} = ${droite}`;
    const candidats = contribs.slice(1).map(c => signeD(-c));
    const payload = makeTexteTrou(phrase,
        '<p><b>Transforme cette expression en suite d\'additions</b> (chaque soustraction devient l\'addition de l\'opposé).</p>',
        { ...opts(L_SUITE), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, candidats).slice(0, 3) });
    payload._meta.params = { contribs, formes };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    simplifier_texte_trou_saisie: genSimplifierSaisie,
    reparentheser_texte_trou_drag: genReparentheserDrag,
    transformer_texte_trou_saisie: genTransformerCalculer,
    suite_texte_trou_drag: genSuiteDrag,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'simplifier', label: L_SIMPLIFIER, formatsList: buildFormats('simplifier') },
    { id: 'reparentheser', label: L_REPARENTHESER, formatsList: buildFormats('reparentheser') },
    { id: 'transformer', label: L_TRANSFORMER, formatsList: buildFormats('transformer') },
    { id: 'suite', label: L_SUITE, formatsList: buildFormats('suite') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
